import { useContext, useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import { terminalOutline } from 'ionicons/icons';
import { WorkflowActionsContext } from '../contexts/WorkflowActionsContext';
import { CartoonPreflight, CartoonExecuteLive, CartoonGenerateScript } from './CartoonWorkflowIcons';
import './WorkflowNode.css';

const EditableLabel = ({ label, editing, setEditing, editValue, setEditValue, commitRename, inputRef, className }) => (
  <div className={`gwe-workflow-node__label-wrap ${className || ''}`}>
    <div
      className="gwe-workflow-node__label gwe-workflow-node__label--editable"
      style={{ visibility: editing ? 'hidden' : 'visible' }}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {label}
    </div>
    {editing && (
      <input
        ref={inputRef}
        className="gwe-workflow-node__label-input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setEditValue(label); setEditing(false); } }}
        onClick={(e) => e.stopPropagation()}
      />
    )}
  </div>
);

export const WorkflowNode = ({ data, isConnectable }) => {
  const {
    label, status = 'pending',
    showAddButton = false, nodeId, toolCount = 0,
    customCode = null, canDelete = false, nodeType
  } = data;
  const hasCustomCode = !!customCode;
  const actions = useContext(WorkflowActionsContext);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef(null);

  useEffect(() => { setEditValue(label); }, [label]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== label) {
      actions?.onRenameNode(nodeId, trimmed);
    } else {
      setEditValue(label);
    }
    setEditing(false);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    actions?.onNodeContextMenu(nodeId, { x: e.clientX, y: e.clientY });
  };

  const statusColors = {
    pending: '#8c8c8c',
    running: '#4a90e2',
    completed: '#52c41a',
    success: '#52c41a',
    failed: '#ff4d4f',
    error: '#ff4d4f',
    cancelled: '#ff9800'
  };

  const statusColor = statusColors[status] || statusColors.pending;
  const nodeIcon = terminalOutline;

  const useCartoonPreflight = nodeType === 'preflight';
  const useCartoonLive = nodeType === 'execute_live';
  const useCartoonScript = nodeType === 'generate_script';

  const rootClass = [
    'gwe-workflow-node',
    'gwe-workflow-node--action',
    status === 'running' && 'gwe-workflow-node--running',
  ].filter(Boolean).join(' ');

  const handles = (
    <>
      <Handle type="target" position={Position.Top} id="top" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="target" position={Position.Left} id="target-left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="target" position={Position.Right} id="target-right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="source" position={Position.Bottom} id="bottom" isConnectable={isConnectable} className="gwe-workflow-node__handle" />
      <Handle type="source" position={Position.Left} id="left" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
      <Handle type="source" position={Position.Right} id="right" isConnectable={isConnectable} className="gwe-workflow-node__handle gwe-workflow-node__handle--side" />
    </>
  );

  return (
    <div
      className={rootClass}
      style={{ borderColor: statusColor }}
      data-node-status={status}
      onContextMenu={handleContextMenu}
    >
      {handles}

      <div className="gwe-workflow-node__action-layout">
        <div className="gwe-workflow-node__icon-box">
          {useCartoonPreflight ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonPreflight status={status} />
            </div>
          ) : useCartoonLive ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonExecuteLive status={status} />
            </div>
          ) : useCartoonScript ? (
            <div className="gwe-workflow-node__cartoon-wrap" aria-hidden>
              <CartoonGenerateScript status={status} />
            </div>
          ) : (
            <IonIcon icon={nodeIcon} className="gwe-workflow-node__icon" />
          )}
        </div>
        <EditableLabel
          label={label} editing={editing} setEditing={setEditing}
          editValue={editValue} setEditValue={setEditValue}
          commitRename={commitRename} inputRef={inputRef}
          className="gwe-workflow-node__label--below"
        />
        {(toolCount > 0 || hasCustomCode) && (
          <div className="gwe-workflow-node__badges">
            {toolCount > 0 && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--tools">
                {toolCount} tool{toolCount > 1 ? 's' : ''}
              </div>
            )}
            {hasCustomCode && (
              <div className="gwe-workflow-node__badge gwe-workflow-node__badge--custom">
                custom
              </div>
            )}
          </div>
        )}
      </div>

      {showAddButton && (
        <button
          className="gwe-workflow-node__add-btn"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            actions?.onRequestAddNode(nodeId, rect);
          }}
          title="Insert node after this step"
        >
          +
        </button>
      )}
    </div>
  );
};
